import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';
import { slideInFromTop } from '../constants';

interface SmartAlertsAnimationProps {
  isActive: boolean;
}

const NOTIFICATIONS = [
  {
    emoji: '🟢',
    title: 'AAPL +4.2% after earnings',
    subtitle: 'Your position: +$847',
    time: 'now',
  },
  {
    emoji: '🔴',
    title: 'Market Alert',
    subtitle: 'Fed announces rate hold',
    time: '2m',
  },
  {
    emoji: '🟢',
    title: 'NVDA hits 52-week high',
    subtitle: 'Consider taking profits',
    time: '5m',
  },
];

export const SmartAlertsAnimation: React.FC<SmartAlertsAnimationProps> = ({ isActive }) => {
  const [visibleNotifications, setVisibleNotifications] = useState<number[]>([]);
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (isActive) {
      setVisibleNotifications([]);

      // Clear any existing timeouts
      timeoutIdsRef.current.forEach((id) => clearTimeout(id));
      timeoutIdsRef.current = [];

      // Stagger notification appearances
      NOTIFICATIONS.forEach((_, index) => {
        const id = setTimeout(() => {
          setVisibleNotifications((prev) => [...prev, index]);
        }, index * 600);
        timeoutIdsRef.current.push(id);
      });
    }

    return () => {
      // Copy ref value to local variable for cleanup
      const timeoutIds = timeoutIdsRef.current;
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [isActive]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.centi};
        padding: ${spacing.deci};
        height: 100%;
        overflow: hidden;
      `}
    >
      {/* Notifications stack */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
          flex: 1;
        `}
      >
        {NOTIFICATIONS.map((notification, index) => {
          const isVisible = visibleNotifications.includes(index);

          return (
            <div
              key={index}
              css={css`
                background: ${colors.sugar};
                border-radius: 12px;
                padding: ${spacing.centi} ${spacing.deci};
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                opacity: ${isVisible ? 1 : 0};
                transform: translateY(${isVisible ? 0 : -20}px);
                animation: ${isVisible ? slideInFromTop : 'none'} 0.4s ease-out forwards;

                html[data-theme='dark'] & {
                  background: #2a2a2a;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  gap: ${spacing.centi};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${spacing.centi};
                    flex: 1;
                  `}
                >
                  <span
                    css={css`
                      font-size: 14px;
                      flex-shrink: 0;
                    `}
                  >
                    {notification.emoji}
                  </span>
                  <div>
                    <div
                      css={css`
                        ${fontStyles.fatCat};
                        color: ${colors.liquorice};

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {notification.title}
                    </div>
                    <div
                      css={css`
                        ${fontStyles.mouse};
                        color: ${colors.tungsten};

                        html[data-theme='dark'] & {
                          color: ${colors.silver};
                        }
                      `}
                    >
                      {notification.subtitle}
                    </div>
                  </div>
                </div>
                <div
                  css={css`
                    ${fontStyles.flea};
                    color: ${colors.cloud};
                    white-space: nowrap;

                    html[data-theme='dark'] & {
                      color: ${colors.tungsten};
                    }
                  `}
                >
                  {notification.time}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
